/**
 * Offer events for the cart cards (cart upgrade, kassakoopje), measured per A/B variant by
 * SP Utilities. Every event goes out as a Shopify customer event:
 *
 *   Shopify.analytics.publish('spu_offer', { offer, action, product_id, value })
 *
 * and as the DOM event `spu:offer` on document with detail { offer, action }. SP Utilities
 * counts the DOM event: its A/B embed script sends it through the app proxy. Its web pixel
 * cannot, because Shopify's strict pixel sandbox refuses requests to the shop's own origin
 * (RestrictedUrlError), and the app proxy lives on that origin. The customer event stays for
 * custom pixels and other listeners.
 *
 * - offer: 'cart_upgrade' or 'kassakoopje'
 * - action: 'shown' (the card was really on screen), 'accept' (added to the cart, with value in
 *   the cart currency), 'dismiss' ("Nee, dankjewel") or 'reopen' (opened again from the text
 *   button)
 *
 * and, when Microsoft Clarity is already loaded, as the Clarity event
 * `spu_offer_<offer>_<action>`. Clarity is never loaded from here.
 *
 * 'shown' is sent at most once per offer per browser session (sessionStorage; without storage
 * once per page), only for a card that is rendered (its A/B class active, not hidden by the
 * one-offer rule), not collapsed, at least half in view (IntersectionObserver) and not inside a
 * closed cart drawer. A card re-rendered after a cart update is not counted again. The control
 * group never sees a card, so it sends nothing.
 *
 * The card scripts (cart-upgrade-offer.js, kassakoopjes.js) register their cards with watch()
 * and send the other actions with publish(). Nothing here may break a card: every call is
 * guarded and wrapped in try/catch.
 */
(function () {
  if (window.SenifoneOfferEvents) return;

  var SHOWN_KEY = 'senifone:spu-offer-shown:';
  var CHECK_DELAY = 400; // the cart drawer fades in over 0.15s; check once it settled
  var watched = []; // { offer, selector, isActive }
  var shownInMemory = {};
  var intersecting = typeof WeakMap === 'function' ? new WeakMap() : null;
  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;
  var intersectionObserver = null;
  var attributeObserver = null;
  var checkTimer = null;

  function number(value) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : undefined;
  }

  // Event data from the card: data-spu-product-id and data-spu-value-cents (both optional).
  function payload(offer, action, card) {
    var data = { offer: offer, action: action };
    var productId = card && card.getAttribute('data-spu-product-id');
    if (productId) data.product_id = productId;
    if (action === 'accept') {
      var cents = number(card && card.getAttribute('data-spu-value-cents'));
      if (cents !== undefined) data.value = Math.round(cents) / 100;
    }
    return data;
  }

  function publish(offer, action, card) {
    var data = null;
    try {
      data = payload(offer, action, card);
    } catch (error) {
      data = { offer: offer, action: action };
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('spu_offer', data);
      }
    } catch (error) {
      // Measurement only; the card keeps working.
    }
    try {
      var detail = { offer: offer, action: action };
      var event;
      if (typeof window.CustomEvent === 'function') {
        event = new window.CustomEvent('spu:offer', { detail: detail });
      } else if (document.createEvent) {
        event = document.createEvent('CustomEvent');
        event.initCustomEvent('spu:offer', false, false, detail);
      }
      if (event) document.dispatchEvent(event);
    } catch (error) {
      // Measurement only.
    }
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', 'spu_offer_' + offer + '_' + action);
      }
    } catch (error) {
      // Measurement only.
    }
  }

  function wasShown(offer) {
    if (shownInMemory[offer]) return true;
    try {
      return window.sessionStorage.getItem(SHOWN_KEY + offer) === '1';
    } catch (error) {
      return false;
    }
  }

  function rememberShown(offer) {
    shownInMemory[offer] = true;
    try {
      window.sessionStorage.setItem(SHOWN_KEY + offer, '1');
    } catch (error) {
      // Once per page only.
    }
  }

  // On screen for the visitor: rendered, not collapsed, in view and not in a closed drawer.
  function isOnScreen(card) {
    if (!card.isConnected || card.offsetParent === null) return false;
    if (card.classList.contains('is-collapsed')) return false;
    if (window.getComputedStyle(card).visibility !== 'visible') return false;
    if (intersecting) return intersecting.get(card) === true;
    var rect = card.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < (window.innerHeight || 0) && rect.width > 0;
  }

  function check() {
    checkTimer = null;
    watched.forEach(function (entry) {
      if (wasShown(entry.offer)) return;
      var cards = document.querySelectorAll(entry.selector);
      for (var i = 0; i < cards.length; i += 1) {
        var card = cards[i];
        var active = false;
        try {
          active = entry.isActive(card) && isOnScreen(card);
        } catch (error) {
          active = false;
        }
        if (active) {
          rememberShown(entry.offer);
          publish(entry.offer, 'shown', card);
          return;
        }
      }
    });
  }

  function scheduleCheck() {
    if (checkTimer) clearTimeout(checkTimer);
    checkTimer = setTimeout(function () {
      try {
        check();
      } catch (error) {
        // Measurement only.
      }
    }, CHECK_DELAY);
  }

  // Watches the cards of offers not yet shown: their position (IntersectionObserver) and the
  // classes that show or hide them (A/B class on <html>, is-collapsed, the open cart drawer).
  function observeCards() {
    var pending = watched.filter(function (entry) {
      return !wasShown(entry.offer);
    });
    if (!pending.length) return;
    if (window.MutationObserver && !attributeObserver) {
      attributeObserver = new MutationObserver(scheduleCheck);
      attributeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    if (window.IntersectionObserver && !intersectionObserver && intersecting) {
      intersectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (item) {
          intersecting.set(item.target, item.isIntersecting && item.intersectionRatio >= 0.5);
        });
        scheduleCheck();
      }, { threshold: [0, 0.5, 1] });
    } else if (!window.IntersectionObserver) {
      intersecting = null;
    }
    pending.forEach(function (entry) {
      document.querySelectorAll(entry.selector).forEach(function (card) {
        if (observed && observed.has(card)) return;
        if (observed) observed.add(card);
        if (intersectionObserver) intersectionObserver.observe(card);
        if (attributeObserver) {
          attributeObserver.observe(card, { attributes: true, attributeFilter: ['class'] });
          var drawer = card.closest('.cc-popup');
          if (drawer) attributeObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }
      });
    });
    scheduleCheck();
  }

  // isActive(card): the card's own rules (A/B class active, not yielding to another offer).
  function watch(offer, selector, isActive) {
    try {
      watched.push({ offer: offer, selector: selector, isActive: isActive || function () { return true; } });
      observeCards();
    } catch (error) {
      // Measurement only.
    }
  }

  // A cart re-render inserts new cards: observe them too (batched, the page mutates often).
  var observeTimer = null;
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      if (!watched.length || observeTimer) return;
      for (var i = 0; i < records.length; i += 1) {
        if (records[i].addedNodes.length) {
          observeTimer = setTimeout(function () {
            observeTimer = null;
            try {
              observeCards();
            } catch (error) {
              // Measurement only.
            }
          }, 100);
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.addEventListener('pageshow', scheduleCheck);

  window.SenifoneOfferEvents = {
    watch: watch,
    publish: publish,
    check: scheduleCheck
  };
})();
